import React, { useState, useRef, useMemo } from 'react';
import useArrowNav from '../../hooks/useArrowNav';
import ContactPicker from '../../components/ContactPicker';
import { Radio, Send, Zap, Key, Globe, PhoneCall, MessageCircle, Mail, Bell, X, ChevronRight, Search } from 'lucide-react';
import { safeAsync, Badge, emptyMsg, cardWrap, btnPrimary, btnSm, inp, neuShadow, neuShadowSm, neuShadowInset, CHANNEL_COLORS, STATUS_COLORS, OUTCOME_COLORS } from './crmShared';
import { matchesNameQuery } from '../../lib/nameSearch';

const MAX_MESSAGES = 50;
const MAX_CALL_LOG = 20;

export default function CommsTab({
  channels, messages, callLog, contacts, apiSettings,
  canCreate, seeding, setSeeding, loadExtended,
  showSendModal, setShowSendModal, sendChannel, setSendChannel,
  sendResult, setSendResult, sendingMsg, setSendingMsg,
  sendPrefill, setSendPrefill
}) {
  const [sending, setSending] = useState(false);
  const sendToRef = useRef(null);  // the to_address (phone/email) input, for auto-fill on contact pick
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [messageSearch, setMessageSearch] = useState('');
  const [callSearch, setCallSearch] = useState('');
  const channelsRef = useRef(null);
  const { onKeyDown: handleChannelsKeyDown } = useArrowNav(channelsRef, '[data-channel-card]', { horizontal: true, vertical: false, activateOnFocus: true });

  // ═══ Message History Accordion Logic ═══
  const messagesByChannel = useMemo(() => {
    const groups = {};
    messages.slice(0, MAX_MESSAGES).forEach(m => {
      const channel = m.channel || 'unknown';
      if (!groups[channel]) groups[channel] = [];
      groups[channel].push(m);
    });
    // Sort messages within each channel by date (newest first)
    Object.keys(groups).forEach(ch => {
      groups[ch].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });
    return groups;
  }, [messages]);

  const filteredMessageChannels = useMemo(() => {
    if (!messageSearch) return messagesByChannel;
    const lowerSearch = messageSearch.toLowerCase();
    const filtered = {};
    Object.entries(messagesByChannel).forEach(([channel, msgs]) => {
      const filtered_msgs = msgs.filter(m =>
        matchesNameQuery([m.first_name, m.last_name, m.subject, m.body], lowerSearch));
      if (filtered_msgs.length > 0) filtered[channel] = filtered_msgs;
    });
    return filtered;
  }, [messagesByChannel, messageSearch]);

  // ═══ Call Log Accordion Logic ═══
  const callsByOutcome = useMemo(() => {
    const groups = {};
    callLog.slice(0, MAX_CALL_LOG).forEach(cl => {
      const outcome = cl.outcome || 'N/A';
      if (!groups[outcome]) groups[outcome] = [];
      groups[outcome].push(cl);
    });
    // Sort calls within each outcome by date (newest first)
    Object.keys(groups).forEach(outcome => {
      groups[outcome].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });
    return groups;
  }, [callLog]);

  const filteredCallOutcomes = useMemo(() => {
    if (!callSearch) return callsByOutcome;
    const lowerSearch = callSearch.toLowerCase();
    const filtered = {};
    Object.entries(callsByOutcome).forEach(([outcome, calls]) => {
      const filtered_calls = calls.filter(cl =>
        matchesNameQuery([cl.first_name, cl.last_name, cl.notes], lowerSearch));
      if (filtered_calls.length > 0) filtered[outcome] = filtered_calls;
    });
    return filtered;
  }, [callsByOutcome, callSearch]);

  const toggleGroup = (groupKey) => {
    const newSet = new Set(expandedGroups);
    if (newSet.has(groupKey)) {
      newSet.delete(groupKey);
    } else {
      newSet.add(groupKey);
    }
    setExpandedGroups(newSet);
  };

  const expandAll = () => {
    const allKeys = new Set();
    Object.keys(filteredMessageChannels).forEach(ch => allKeys.add(`msg-${ch}`));
    Object.keys(filteredCallOutcomes).forEach(outcome => allKeys.add(`call-${outcome}`));
    setExpandedGroups(allKeys);
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const isSearchActive = messageSearch !== '' || callSearch !== '';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Radio size={18} color="#2E8B98" /> Multichannel Communications
        </h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {canCreate && channels.length === 0 && (
            <button onClick={safeAsync(async () => { setSeeding(true); await window.zignagenixAPI.seedDefaultChannels(); await loadExtended(); setSeeding(false); })}
              disabled={seeding} style={btnPrimary}>
              <Zap size={13} /> Seed Channels
            </button>
          )}
          {canCreate && (
            <button onClick={() => { setSendResult(null); setShowSendModal(true); }} style={btnPrimary}><Send size={13} /> Send Message</button>
          )}
        </div>
      </div>

      {/* API Connection Status Banner */}
      {(() => {
        const twilioActive = apiSettings.find(s => s.provider === 'twilio' && s.is_active);
        const emailActive = apiSettings.find(s => s.provider === 'sendgrid' && s.is_active) || apiSettings.find(s => s.provider === 'smtp' && s.is_active);
        const anyActive = twilioActive || emailActive;
        return (
          <div style={{ ...cardWrap, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <Globe size={16} color={anyActive ? '#62BB46' : '#F26522'} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#212529' }}>
                API Status: {anyActive ? 'Connected' : 'Not Configured'}
              </div>
              <div style={{ fontSize: 10, color: '#6C757D' }}>
                {twilioActive ? '✓ Twilio (SMS/WhatsApp/Voice)' : '✗ Twilio not configured'}
                {' · '}
                {emailActive ? '✓ Email configured' : '✗ Email not configured'}
                {!anyActive && ' — Go to API Settings tab to connect your providers'}
              </div>
            </div>
            {!anyActive && <button onClick={() => { const el = document.querySelector('[data-tab="api_settings"]'); if (el) el.click(); }} style={btnSm}><Key size={12} /> Configure</button>}
          </div>
        );
      })()}

      {/* Channel Cards */}
      {channels.length > 0 && (
        <div ref={channelsRef} onKeyDown={handleChannelsKeyDown} style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          {channels.map(ch => {
            const icons = { sms: MessageCircle, email: Mail, whatsapp: MessageCircle, in_app: Bell, voice: PhoneCall };
            const colors = { sms: '#62BB46', email: '#2E8B98', whatsapp: '#62BB46', in_app: '#256F7A', voice: '#F26522' };
            const I = icons[ch.channel_type] || Radio;
            const clr = colors[ch.channel_type] || '#6C757D';
            const chMsgs = messages.filter(m => m.channel === ch.channel_type);
            const sent = chMsgs.filter(m => m.status === 'sent' || m.status === 'delivered' || m.status === 'read').length;
            const failed = chMsgs.filter(m => m.status === 'failed').length;
            return (
              <div key={ch.id} data-channel-card tabIndex={0} style={{ background: '#F8F9FA', borderRadius: 14, boxShadow: neuShadow, padding: '16px 20px', minWidth: 150, flex: '1 1 150px', cursor: 'pointer', outline: 'none' }}
                onClick={() => { setSendChannel(ch.channel_type); setSendResult(null); setShowSendModal(true); }}
                onFocus={(e) => { e.currentTarget.style.boxShadow = `${neuShadow}, 0 0 0 2px #2E8B98`; }}
                onBlur={(e) => { e.currentTarget.style.boxShadow = neuShadow; }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 10, background: `${clr}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <I size={16} color={clr} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#212529' }}>{ch.name}</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: clr }}>{chMsgs.length}</div>
                <div style={{ display: 'flex', gap: 8, fontSize: 10, color: '#6C757D' }}>
                  <span>✓ {sent} sent</span>
                  {failed > 0 && <span style={{ color: '#DC3545' }}>✗ {failed} failed</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Unified Inbox with Accordions */}
      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ flex: 2 }}>
          {/* Message History Accordion */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#212529', margin: 0 }}>Message History</h4>
              <div style={{ display: 'flex', gap: 8 }}>
                {(Object.keys(filteredMessageChannels).length > 0 || Object.keys(messagesByChannel).length > 0) && (
                  <>
                    <button onClick={expandAll} style={{ ...btnSm, padding: '4px 10px', fontSize: 11 }}>Expand All</button>
                    <button onClick={collapseAll} style={{ ...btnSm, padding: '4px 10px', fontSize: 11 }}>Collapse All</button>
                  </>
                )}
              </div>
            </div>

            {/* Message Search Bar */}
            <div style={{ marginBottom: 12, position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: '#ADB5BD' }} />
              <input
                type="text"
                placeholder="Search messages by contact, subject, or body…"
                value={messageSearch}
                onChange={(e) => {
                  setMessageSearch(e.target.value);
                  if (e.target.value && !isSearchActive) {
                    expandAll();
                  }
                }}
                style={{ ...inp, paddingLeft: 38, width: '100%' }}
              />
            </div>

            {messages.length === 0 ? (
              <div style={cardWrap}>
                <div style={emptyMsg}>No messages yet. Click "Send Message" or click a channel card to compose.</div>
              </div>
            ) : Object.keys(filteredMessageChannels).length === 0 ? (
              <div style={cardWrap}>
                <div style={emptyMsg}>No messages match your search.</div>
              </div>
            ) : (
              Object.entries(filteredMessageChannels).map(([channel, msgs]) => {
                const groupKey = `msg-${channel}`;
                const isExpanded = expandedGroups.has(groupKey) || isSearchActive;
                const channelColor = CHANNEL_COLORS[channel] || '#6C757D';
                return (
                  <div key={groupKey} style={{ marginBottom: 12, borderRadius: 10, overflow: 'hidden', boxShadow: neuShadowSm }}>
                    {/* Accordion Header */}
                    <div
                      onClick={() => toggleGroup(groupKey)}
                      style={{
                        padding: '12px 16px',
                        background: '#F8F9FA',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        userSelect: 'none',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#EEEFF2'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#F8F9FA'}
                    >
                      <ChevronRight
                        size={16}
                        color={channelColor}
                        style={{
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                          flexShrink: 0
                        }}
                      />
                      <Badge text={channel.toUpperCase()} color={channelColor} />
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#212529', flex: 1 }}>
                        {channel.charAt(0).toUpperCase() + channel.slice(1)}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, background: channelColor, color: '#fff', padding: '2px 8px', borderRadius: 4 }}>
                        {msgs.length}
                      </span>
                    </div>

                    {/* Accordion Content */}
                    <div
                      style={{
                        maxHeight: isExpanded ? '1000px' : '0px',
                        overflow: 'hidden',
                        transition: 'maxHeight 0.3s ease-in-out',
                        background: '#fff'
                      }}
                    >
                      {msgs.map((m, i) => {
                        const meta = typeof m.metadata === 'string' ? (() => { try { return JSON.parse(m.metadata); } catch { return {}; } })() : (m.metadata || {});
                        return (
                          <div key={m.id} style={{ padding: '10px 16px', borderBottom: i < msgs.length - 1 ? '1px solid #dce2e8' : 'none', display: 'flex', gap: 10, alignItems: 'flex-start', background: i % 2 === 1 ? '#F8F9FA' : '#fff' }}>
                            <div style={{ width: 6, height: 6, borderRadius: 3, background: m.direction === 'outbound' ? '#2E8B98' : '#62BB46', marginTop: 6, flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 3, flexWrap: 'wrap' }}>
                                <Badge text={(m.channel || '').toUpperCase()} color={CHANNEL_COLORS[m.channel] || '#6C757D'} />
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>{m.first_name} {m.last_name}</span>
                                <Badge text={m.status} color={STATUS_COLORS[m.status] || '#6C757D'} />
                                {meta.provider_id && <span style={{ fontSize: 9, color: '#ADB5BD', fontFamily: 'monospace' }}>ID: {meta.provider_id.substring(0, 12)}…</span>}
                                {m.status === 'failed' && meta.delivery_error && (
                                  <span style={{ fontSize: 9, color: '#DC3545' }} title={meta.delivery_error}>⚠ {meta.delivery_error.substring(0, 40)}</span>
                                )}
                                <span style={{ fontSize: 10, color: '#ADB5BD', marginLeft: 'auto' }}>{m.direction === 'outbound' ? '→' : '←'} {new Date(m.created_at).toLocaleString()}</span>
                              </div>
                              {m.subject && <div style={{ fontSize: 11, fontWeight: 600, color: '#212529' }}>{m.subject}</div>}
                              <div style={{ fontSize: 11, color: '#6C757D' }}>{m.body?.substring(0, 150)}{m.body?.length > 150 ? '…' : ''}</div>
                              {m.to_address && <div style={{ fontSize: 9, color: '#ADB5BD', marginTop: 2 }}>To: {m.to_address}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Call Log Accordion */}
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#212529', margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                <PhoneCall size={14} color="#F26522" /> Call Log
              </h4>
              <div style={{ display: 'flex', gap: 8 }}>
                {(Object.keys(filteredCallOutcomes).length > 0 || Object.keys(callsByOutcome).length > 0) && (
                  <>
                    <button onClick={expandAll} style={{ ...btnSm, padding: '4px 10px', fontSize: 11 }}>Expand All</button>
                    <button onClick={collapseAll} style={{ ...btnSm, padding: '4px 10px', fontSize: 11 }}>Collapse All</button>
                  </>
                )}
              </div>
            </div>

            {canCreate && (
              <button onClick={safeAsync(async () => {
                const contactId = prompt('Contact ID:');
                if (!contactId) return;
                const direction = prompt('Direction (inbound/outbound):', 'outbound') || 'outbound';
                const outcome = prompt('Outcome (answered/voicemail/no_answer/busy/callback_requested):', 'answered') || 'answered';
                const duration = parseInt(prompt('Duration (seconds):', '120') || '0');
                const notes = prompt('Notes:') || '';
                await window.zignagenixAPI.logCRMCall({ contact_id: parseInt(contactId), direction, outcome, duration_seconds: duration, notes });
                loadExtended();
              })} style={{ ...btnSm, marginBottom: 10, width: '100%', justifyContent: 'center' }}><PhoneCall size={12} /> Log Call</button>
            )}

            {/* Call Search Bar */}
            <div style={{ marginBottom: 12, position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: '#ADB5BD' }} />
              <input
                type="text"
                placeholder="Search calls by contact or notes…"
                value={callSearch}
                onChange={(e) => {
                  setCallSearch(e.target.value);
                  if (e.target.value && !isSearchActive) {
                    expandAll();
                  }
                }}
                style={{ ...inp, paddingLeft: 38, width: '100%' }}
              />
            </div>

            {callLog.length === 0 ? (
              <div style={cardWrap}>
                <div style={emptyMsg}>No calls logged yet.</div>
              </div>
            ) : Object.keys(filteredCallOutcomes).length === 0 ? (
              <div style={cardWrap}>
                <div style={emptyMsg}>No calls match your search.</div>
              </div>
            ) : (
              Object.entries(filteredCallOutcomes).map(([outcome, calls]) => {
                const groupKey = `call-${outcome}`;
                const isExpanded = expandedGroups.has(groupKey) || isSearchActive;
                const outcomeColor = OUTCOME_COLORS[outcome] || '#6C757D';
                return (
                  <div key={groupKey} style={{ marginBottom: 12, borderRadius: 10, overflow: 'hidden', boxShadow: neuShadowSm }}>
                    {/* Accordion Header */}
                    <div
                      onClick={() => toggleGroup(groupKey)}
                      style={{
                        padding: '12px 16px',
                        background: '#F8F9FA',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        userSelect: 'none',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#EEEFF2'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#F8F9FA'}
                    >
                      <ChevronRight
                        size={16}
                        color={outcomeColor}
                        style={{
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                          flexShrink: 0
                        }}
                      />
                      <Badge text={outcome} color={outcomeColor} />
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#212529', flex: 1 }}>
                        {outcome.charAt(0).toUpperCase() + outcome.slice(1).replace(/_/g, ' ')}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, background: outcomeColor, color: '#fff', padding: '2px 8px', borderRadius: 4 }}>
                        {calls.length}
                      </span>
                    </div>

                    {/* Accordion Content */}
                    <div
                      style={{
                        maxHeight: isExpanded ? '1000px' : '0px',
                        overflow: 'hidden',
                        transition: 'maxHeight 0.3s ease-in-out',
                        background: '#fff'
                      }}
                    >
                      {calls.map((cl, i) => (
                        <div key={cl.id} style={{ padding: '8px 12px', borderBottom: i < calls.length - 1 ? '1px solid #dce2e8' : 'none', background: i % 2 === 1 ? '#F8F9FA' : '#fff' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>{cl.first_name} {cl.last_name}</span>
                            <Badge text={cl.outcome || 'N/A'} color={OUTCOME_COLORS[cl.outcome] || '#6C757D'} />
                          </div>
                          <div style={{ fontSize: 10, color: '#6C757D' }}>
                            {cl.direction === 'outbound' ? '→' : '←'} {cl.duration_seconds ? `${Math.floor(cl.duration_seconds / 60)}m ${cl.duration_seconds % 60}s` : '0s'}
                            {cl.notes && ` — ${cl.notes.substring(0, 60)}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* ═══ SEND MESSAGE MODAL ═══ */}
      {showSendModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => { setShowSendModal(false); if (setSendPrefill) setSendPrefill(null); }}>
          <div style={{ background: '#F8F9FA', borderRadius: 20, boxShadow: '12px 12px 24px rgba(163,177,198,0.6), -12px -12px 24px rgba(255,255,255,0.9)', padding: 28, width: 520, maxHeight: '80vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#212529', margin: 0 }}>Send Message</h3>
              <button onClick={() => { setShowSendModal(false); if (setSendPrefill) setSendPrefill(null); }} style={{ ...btnSm, padding: '4px 8px' }}><X size={14} /></button>
            </div>

            {/* Channel selector */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              {['sms', 'email', 'whatsapp', 'in_app', 'voice'].map(c => {
                const labels = { sms: 'SMS', email: 'Email', whatsapp: 'WhatsApp', in_app: 'In-App', voice: 'Voice' };
                const colors = { sms: '#62BB46', email: '#2E8B98', whatsapp: '#62BB46', in_app: '#256F7A', voice: '#F26522' };
                return (
                  <button key={c} onClick={() => setSendChannel(c)}
                    style={{ ...btnSm, background: sendChannel === c ? colors[c] : '#F8F9FA', color: sendChannel === c ? '#fff' : '#6C757D',
                      boxShadow: sendChannel === c ? neuShadowInset : neuShadowSm, fontWeight: sendChannel === c ? 700 : 500 }}>
                    {labels[c]}
                  </button>
                );
              })}
            </div>

            <form key={`${sendChannel}-${sendPrefill?.contactId || ''}-${sendPrefill?.toAddress || ''}`} onSubmit={async (e) => {
              e.preventDefault();
              setSending(true); setSendingMsg(true); setSendResult(null);
              const fd = new FormData(e.target);
              try {
                const res = await window.zignagenixAPI.sendCRMMessage({
                  contact_id: parseInt(fd.get('contact_id')),
                  channel: sendChannel,
                  to_address: fd.get('to_address') || '',
                  subject: fd.get('subject') || '',
                  body: fd.get('body') || '',
                });
                setSendResult(res);
                if (res.success) { loadExtended(); }
              } catch (err) { setSendResult({ success: false, error: err.message }); }
              finally { setSending(false); setSendingMsg(false); }
            }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 4, display: 'block' }}>Contact</label>
                <ContactPicker
                  contacts={contacts}
                  name="contact_id"
                  defaultContactId={sendPrefill?.contactId || ''}
                  inputStyle={inp}
                  onSelect={(c) => {
                    if (sendToRef.current) {
                      sendToRef.current.value = sendChannel === 'email' ? (c.email || '') : (c.phone || '');
                    }
                  }}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 4, display: 'block' }}>
                  {sendChannel === 'email' ? 'Email Address' : sendChannel === 'in_app' ? 'Notification Title' : 'Phone Number'}
                </label>
                <input ref={sendToRef} name="to_address" defaultValue={sendPrefill?.toAddress || ''}
                  placeholder={sendChannel === 'email' ? 'name@example.com' : sendChannel === 'in_app' ? '(optional)' : '+1 (555) 000-0000'}
                  style={inp} required={sendChannel !== 'in_app'} />
              </div>
              {sendChannel === 'email' && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 4, display: 'block' }}>Subject</label>
                  <input name="subject" placeholder="Email subject line" style={inp} />
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 4, display: 'block' }}>
                  {sendChannel === 'voice' ? 'Voice Message (TwiML Say)' : 'Message Body'}
                </label>
                <textarea name="body" rows={4} placeholder={sendChannel === 'voice' ? 'Text to be spoken to the recipient…' : 'Type your message…'}
                  style={{ ...inp, resize: 'vertical' }} required />
              </div>

              {/* Result feedback */}
              {sendResult && (
                <div style={{ padding: '10px 14px', borderRadius: 10, marginBottom: 12,
                  background: sendResult.success ? '#dcfce7' : '#fef2f2',
                  color: sendResult.success ? '#166534' : '#991b1b', fontSize: 12 }}>
                  {sendResult.success ? (
                    <span>✓ Message sent successfully! Status: <strong>{sendResult.status}</strong></span>
                  ) : (
                    <span>✗ Failed{sendResult.status ? ` (status: ${sendResult.status})` : ''}: {sendResult.error || 'No error message was returned. Check the Twilio console logs and the server logs.'}</span>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => { setShowSendModal(false); if (setSendPrefill) setSendPrefill(null); }} style={btnSm}>Cancel</button>
                <button type="submit" disabled={sending} style={btnPrimary}>
                  {sending ? 'Sending…' : <><Send size={13} /> Send via {sendChannel.toUpperCase()}</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
